import { ChangeDetectorRef, Component, Inject, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '@app/core/services/UserService';
import { OrganizationService } from '@app/core/services/OrganizationService';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { SalaryRecieverEditComponent } from '../../../static/users/edit-salaryreciever-modal/edit-salaryreciever-modal.component';
import { SalaryCalculatorDialogComponent } from '../salary-calculator/salary-calculator-dialog.component';
import { finalize, timeout } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'add-payment-recievers-dialog',
  templateUrl: 'add-payment-recievers-Dialog.Component.html'
})

export class AddPaymentRecieversDialogComponent {

  public invoiceGroupTemplate = {
    Id: 0,
    Amount: null,
    Vat: '25',
    Description: '',
    EmploymentTypeId: 2,
    PaymentRecievers: [],
    SelectedPaymentRecievers: [],
    CalculatedAmounts: {},
    SalaryCalculator: { mode: 'invoice', salaryType: 'monthly', amount: null, hours: null, vacationMode: 'included', pensionMode: 'deduct', pensionPercent: 0 } as any
  };

  paymentRecievers: Array<any> = [];
  isLoadingPaymentRecievers = true;

  vatList = [{ Value: "25" }, { Value: "12" }, { Value: "6" }, { Value: "0" }];

  searchTerm: string = '';
  filteredPaymentRecievers: any[] = [];
  selectedPaymentRecieverKeys: Array<string | number> = [];
  private pendingPaymentRecieverPersonalNumber: string | null = null;
  calculatedAmounts: { [key: string]: any } = {};
  salaryCalculatorSettingsExpanded = false;

  constructor(
    public dialogRef: MatDialogRef<AddPaymentRecieversDialogComponent>,
    private userService: UserService,
    public dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private authenticationService: AuthenticationService,
    private organizationService: OrganizationService,
    @Inject(MAT_DIALOG_DATA) public data: any
  )
  {}

  ngOnInit() {
    setTimeout(() => this.getPaymentRecievers(), 0);
  }

  getSalaryCalculatorBaseAmount(): number {
    const amount = Number(this.invoiceGroupTemplate.SalaryCalculator?.amount || 0);
    const hours = Number(this.invoiceGroupTemplate.SalaryCalculator?.hours || 0);
    return amount > 0 && hours > 0 ? amount * hours : 0;
  }

  hasSelectedPaymentRecievers(): boolean {
    return this.selectedPaymentRecieverKeys.length > 0;
  }

  getPreliminarySalaryCalculatorAmount(): number {
    const calculator = this.invoiceGroupTemplate.SalaryCalculator || {};
    const amount = Number(calculator.amount || 0);
    const baseAmount = calculator.salaryType === 'hourly'
      ? this.getSalaryCalculatorBaseAmount()
      : amount;

    if (calculator.mode !== 'gross' || baseAmount <= 0) {
      return baseAmount;
    }

    const vacationAmount = calculator.vacationMode === 'added' ? baseAmount * 0.12 : 0;
    const pensionBase = baseAmount + vacationAmount;
    const pensionAmount = calculator.pensionMode === 'add'
      ? Math.round(pensionBase * (Number(calculator.pensionPercent || 0) / 100)) * 1.2426
      : 0;

    return baseAmount + vacationAmount + pensionAmount;
  }

  onInvoiceAmountChanged(value: number): void {
    this.invoiceGroupTemplate.Amount = value;
  }

  onSalaryCalculatorModeChanged(): void {
    if (this.invoiceGroupTemplate.SalaryCalculator.mode === 'invoice') {
      this.invoiceGroupTemplate.SalaryCalculator.salaryType = 'monthly';
      this.invoiceGroupTemplate.SalaryCalculator.amount = null;
      this.invoiceGroupTemplate.SalaryCalculator.hours = null;
      this.invoiceGroupTemplate.SalaryCalculator.pensionPercent = 0;
      this.invoiceGroupTemplate.SalaryCalculator.vacationMode = 'included';
      this.invoiceGroupTemplate.SalaryCalculator.pensionMode = 'deduct';
      this.invoiceGroupTemplate.Amount = null;
    }
  }

  onPensionPercentChanged(value: number): void {
    const numericValue = Number(value);
    this.invoiceGroupTemplate.SalaryCalculator.pensionPercent = isFinite(numericValue)
      ? Math.max(0, Math.min(35, numericValue))
      : 0;
  }

  filterPaymentReceivers() {
    if (this.searchTerm) {
      this.filteredPaymentRecievers = this.paymentRecievers.filter(item =>
        `${item.FirstName} ${item.LastName} (${item.PersonalNumber})`
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase())
      );
    } else {
      this.filteredPaymentRecievers = [...this.paymentRecievers];
    }
  }

  // Reset the search term when the dropdown is closed or opened
  resetSearchTerm(isOpen: boolean) {
    if (!isOpen) {
      this.searchTerm = '';
      this.filteredPaymentRecievers = [...this.paymentRecievers];
    }
  }

  comparePaymentRecieverSelection = (left: any, right: any): boolean => {
    const leftKey = this.normalizeSelection(left);
    const rightKey = this.normalizeSelection(right);

    if (leftKey == null || rightKey == null) {
      return left === right;
    }

    return String(leftKey) === String(rightKey);
  };

  getPaymentRecieverSelectionValue(item: any): string | number | null {
    return this.normalizeSelection(item);
  }

  isPaymentRecieverSelected(item: any): boolean {
    const key = this.getPaymentRecieverSelectionValue(item);
    if (key == null) {
      return false;
    }

    return this.selectedPaymentRecieverKeys.some(selectedKey =>
      this.comparePaymentRecieverSelection(selectedKey, key)
    );
  }

  onPaymentRecieverOptionSelectionChange(item: any, isSelected: boolean, isUserInput: boolean): void {
    if (!isUserInput) {
      return;
    }

    const key = this.getPaymentRecieverSelectionValue(item);
    if (key == null) {
      return;
    }

    if (isSelected) {
      if (!this.selectedPaymentRecieverKeys.some(selectedKey => this.comparePaymentRecieverSelection(selectedKey, key))) {
        this.selectedPaymentRecieverKeys = [...this.selectedPaymentRecieverKeys, key];
      }
    } else {
      this.selectedPaymentRecieverKeys = this.selectedPaymentRecieverKeys.filter(selectedKey =>
        !this.comparePaymentRecieverSelection(selectedKey, key)
      );
    }

    this.invoiceGroupTemplate.SelectedPaymentRecievers = [...this.selectedPaymentRecieverKeys];
  }

  getSelectedPaymentRecieverTriggerText(): string {
    if (!this.selectedPaymentRecieverKeys || this.selectedPaymentRecieverKeys.length === 0) {
      return '';
    }

    const selectedNames: string[] = [];
    for (const paymentReciever of this.paymentRecievers) {
      if (this.isPaymentRecieverSelected(paymentReciever)) {
        selectedNames.push(`${paymentReciever.FirstName} ${paymentReciever.LastName} (${paymentReciever.PersonalNumber})`);
      }
    }

    if (selectedNames.length === 0) {
      return `Valj betalmottagare (${this.selectedPaymentRecieverKeys.length})`;
    }

    return selectedNames.join(', ');
  }

  onPaymentRecieverSelectionChange(selectedValues: any[]): void {
    const previousSelections = Array.isArray(this.selectedPaymentRecieverKeys)
      ? [...this.selectedPaymentRecieverKeys]
      : [];

    if (!Array.isArray(selectedValues)) {
      this.selectedPaymentRecieverKeys = [];
      this.invoiceGroupTemplate.SelectedPaymentRecievers = [];
      return;
    }

    const visibleSelectionKeys = new Set<string>();
    for (const paymentReciever of this.filteredPaymentRecievers) {
      const key = this.getPaymentRecieverSelectionValue(paymentReciever);
      if (key != null) {
        visibleSelectionKeys.add(String(key));
      }
    }

    const preservedHiddenSelections = previousSelections.filter(selection => {
      const key = this.normalizeSelection(selection);
      return key != null && !visibleSelectionKeys.has(String(key));
    });

    const dedupedSelections: Array<string | number> = [];
    for (const selectedValue of [...preservedHiddenSelections, ...selectedValues]) {
      const alreadyAdded = dedupedSelections.some(current =>
        this.comparePaymentRecieverSelection(current, selectedValue)
      );

      if (!alreadyAdded) {
        dedupedSelections.push(selectedValue);
      }
    }

    this.selectedPaymentRecieverKeys = dedupedSelections;
    this.invoiceGroupTemplate.SelectedPaymentRecievers = [...dedupedSelections];
  }

  onSubmit() {
    if (!this.hasSelectedPaymentRecievers()) {
      return;
    }

    if (this.invoiceGroupTemplate.SalaryCalculator?.mode === 'gross') {
      const preliminaryAmount = this.getPreliminarySalaryCalculatorAmount();
      this.invoiceGroupTemplate.Amount = preliminaryAmount;
      this.invoiceGroupTemplate.SalaryCalculator.preliminaryAmount = preliminaryAmount;
    }

    const selectedKeys = new Set<string>();
    for (var i = 0; i < this.selectedPaymentRecieverKeys.length; i++) {
      const selected = this.selectedPaymentRecieverKeys[i];
      const normalized = this.normalizeSelection(selected);
      if (normalized != null) {
        selectedKeys.add(String(normalized));
      }
    }

    for (var ii = 0; ii < this.paymentRecievers.length; ii++) {
      var currentUser = this.paymentRecievers[ii];
      const currentUserId = this.normalizeSelection(currentUser);
      if (currentUserId != null && selectedKeys.has(String(currentUserId))) {
        this.invoiceGroupTemplate.PaymentRecievers.push(currentUser);
        selectedKeys.delete(String(currentUserId));
      }
    }

    this.invoiceGroupTemplate.SelectedPaymentRecievers = [...this.selectedPaymentRecieverKeys];
    this.dialogRef.close(this.invoiceGroupTemplate);
  }

  openSalaryCalculator(): void {
    const selectedRecipients = this.paymentRecievers.filter(item => this.isPaymentRecieverSelected(item));
    if (selectedRecipients.length === 0) {
      return;
    }

    const dialogRef = this.dialog.open(SalaryCalculatorDialogComponent, {
      width: '720px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 32px)',
      data: {
        recipients: selectedRecipients,
        employmentTypeId: this.invoiceGroupTemplate.EmploymentTypeId,
        serviceFeeFactor: this.data?.serviceFeeFactor || 0
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.calculations) { return; }
      result.calculations.forEach(calculation => this.calculatedAmounts[String(calculation.recipient.Id)] = calculation);
      this.invoiceGroupTemplate.SalaryCalculator = { salaryType: result.salaryType, amount: result.amount, hours: result.hours };
      if (result.calculations.length === 1) {
        this.invoiceGroupTemplate.Amount = result.calculations[0].invoiceAmount;
      }
    });
  }

  getPaymentRecievers() {
    const organizationId = this.resolveCurrentOrganizationId();

    if (organizationId <= 0) {
      this.onPaymentRecieversError();
      this.setLoadingState(false);
      return;
    }

    this.setLoadingState(true);

    this.organizationService.getPaymentRecievers(organizationId)
      .pipe(timeout(20000))
      .pipe(finalize(() => this.onPaymentRecieversComplete()))
      .subscribe(
        response => {
          this.ngZone.run(() => this.onPaymentRecieversReturned(response));
        },
        () => {
          this.ngZone.run(() => this.onPaymentRecieversError());
        }
      );
  }

  onPaymentRecieversReturned(paymentRecievers) {
    this.paymentRecievers = [];
    this.filteredPaymentRecievers = [];

    if (paymentRecievers && paymentRecievers.length > 0) {
      this.paymentRecievers = [...paymentRecievers];
      this.filteredPaymentRecievers = [...paymentRecievers];
    }

    if (this.pendingPaymentRecieverPersonalNumber) {
      const pendingPersonalNumber = this.pendingPaymentRecieverPersonalNumber;
      const addedPaymentReciever = this.paymentRecievers.find(item =>
        String(item?.PersonalNumber || '').trim() === pendingPersonalNumber
      );
      const selectedValue = this.getPaymentRecieverSelectionValue(addedPaymentReciever);

      if (selectedValue != null && !this.selectedPaymentRecieverKeys.some(value =>
        this.comparePaymentRecieverSelection(value, selectedValue)
      )) {
        this.selectedPaymentRecieverKeys = [...this.selectedPaymentRecieverKeys, selectedValue];
        this.invoiceGroupTemplate.SelectedPaymentRecievers = [...this.selectedPaymentRecieverKeys];
      }

      this.pendingPaymentRecieverPersonalNumber = null;
    }

    this.refreshView();
  }

  onPaymentRecieversError() {
    this.paymentRecievers = [];
    this.filteredPaymentRecievers = [];
    this.pendingPaymentRecieverPersonalNumber = null;
    this.refreshView();
  }

  private resolveCurrentOrganizationId(): number {
    const dialogOrganizationId = Number(this.data?.organizationId);
    if (Number.isFinite(dialogOrganizationId) && dialogOrganizationId > 0) {
      return dialogOrganizationId;
    }

    const currentOrg = this.authenticationService.getCurrentUserOrganization();
    if (currentOrg == null || currentOrg.Id == null) {
      return 0;
    }

    return Number(currentOrg.Id);
  }

  private onPaymentRecieversComplete() {
    this.ngZone.run(() => {
      this.setLoadingState(false);
      this.refreshView();
    });
  }

  private setLoadingState(isLoading: boolean): void {
    this.isLoadingPaymentRecievers = isLoading;
  }

  createSalaryReciever(event: MouseEvent) {
    event.preventDefault();
    const organizationId = this.resolveCurrentOrganizationId();
    if (organizationId <= 0) {
      return;
    }

    const dialogRef = this.dialog.open(SalaryRecieverEditComponent, {
      width: '440px',
      maxWidth: 'calc(100vw - 32px)',
      height: 'auto',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'salary-receiver-dialog',
      data: {
        id: 0,
        headline: 'Ny betalmottagare',
        organizationId
      }
    });

    dialogRef.afterClosed().subscribe(result => {

      if (result != null) {
        this.ngZone.run(() => {
          const personalNumber = String(result?.PersonalNumber || result?.personalNumber || '').trim();
          if (personalNumber === '') {
            return;
          }

          this.pendingPaymentRecieverPersonalNumber = personalNumber;
          this.getPaymentRecievers();
        });
      }

    });
  }

  private refreshView(): void {
    try {
      this.changeDetectorRef.detectChanges();
    } catch {
      // Ignore refresh attempts after the dialog has started tearing down.
    }
  }

  private extractPaymentRecieverId(value: any): any {
    if (value == null) {
      return null;
    }

    const direct = value.Id ?? value.id;
    if (direct != null && direct !== '') {
      return direct;
    }

    const alt = value.UserId ?? value.userId;
    if (alt != null && alt !== '') {
      return alt;
    }

    return null;
  }

  private normalizeSelection(value: any): string | number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      if (normalized === '') {
        return null;
      }
      return normalized;
    }

    const extracted = this.extractPaymentRecieverId(value);
    if (extracted != null && extracted !== '') {
      const asNumber = Number(extracted);
      return isNaN(asNumber) ? String(extracted) : asNumber;
    }

    if (value != null && value.PersonalNumber != null) {
      return String(value.PersonalNumber);
    }

    if (value != null && value.personalNumber != null) {
      return String(value.personalNumber);
    }

    return null;
  }
  GetPersonFromSpar(personalNumber: string) {

    if (personalNumber == null || personalNumber == "") {
      return;
    }

    personalNumber = personalNumber.replace("-", "");

    this.userService.getPersonFromSparByPersonalNumber(personalNumber)
      .subscribe(
        response => this.onGetPersonFromSparCompleted(response, personalNumber),
        error => this.onGetPersonFromSparError(error));
  }

  onGetPersonFromSparError(error) {
    alert('Err')
  }

  onGetPersonFromSparCompleted(response, term) {

  }

  //cancel(response: any) {

  //  this.dialogRef.close(response);
  //}

  cancel() {

    this.dialogRef.close();
  }

}



